import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BarChart2, Layers, FileSearch, Plus, Radar, Lightbulb, MessageSquare } from "lucide-react";
import { useCompareBucket } from "../context/CompareBucketContext";
import { useOwnedGrids } from "../context/OwnedGridsContext";

const ITEMS = [
  { label: "ANALISIS",     icon: BarChart2,  path: "/analysis" },
  { label: "PERBANDINGAN", icon: Layers,     path: "/compare",        badge: "bucket" },
  { label: "LAPORAN",      icon: FileSearch, path: "/deep-analysis",  badge: "owned" },
  { label: "SKENARIO",     icon: Lightbulb,  path: "/skenario",       pro: false },
  { label: "RADAR",        icon: Radar,      path: "/branch-radar", pro: false },
  { label: "ASISTEN",      icon: MessageSquare, path: "/assistant" },
];

export default function Sidebar() {
  const [soon, setSoon] = useState(false);
  const navigate     = useNavigate();
  const { pathname } = useLocation();
  const isActive     = (path) => pathname === path;
  const { bucket }   = useCompareBucket();
  const { ownedIds } = useOwnedGrids();

  const badgeCount = (kind) =>
    kind === "bucket" ? bucket.length
    : kind === "owned" ? ownedIds.length
    : 0;
  // Every count badge on the platform is this one colour, header and sidebar alike:
  // gold, the brand clay (#a24a1e) rotated off the red band to #9a6d16. Two of the
  // header badges show these very same numbers, so a second hue only implied a
  // distinction that is not there. Red is reserved for genuine failure states.
  // White on gold is 4.6:1; the amber-400 and emerald-500 it replaces were 1.7:1
  // and 2.5:1, which left the counts effectively unreadable.
  const BADGE_CLASS = "bg-gold-500";

  return (
    <aside className="absolute left-2.5 top-[84px] bottom-2.5 z-40 w-20 bg-white/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-100/50 flex flex-col items-center py-4 gap-1">
      {ITEMS.map(({ label, icon: Icon, path, pro, dev, badge }) => {
        const count = badge ? badgeCount(badge) : 0;
        return (
        <button
          key={label}
          onClick={() => navigate(path)}
          className={`relative flex flex-col items-center gap-1 px-2 py-3 rounded-xl w-16 transition-colors ${
            isActive(path) ? "text-[#14543e]" : "text-gray-400 hover:text-gray-600"
          }`}
        >
          {pro && (
            <span className="absolute top-1.5 right-1.5 text-[6px] font-extrabold bg-[#14543e] text-white px-1 py-0.5 rounded uppercase tracking-wide leading-none">
              PRO
            </span>
          )}
          {dev && (
            <span className="absolute top-1.5 right-1.5 text-[6px] font-extrabold bg-orange-500 text-white px-1 py-0.5 rounded uppercase tracking-wide leading-none">
              DEV
            </span>
          )}
          {count > 0 && (
            <span className={`absolute top-1.5 right-2.5 min-w-[16px] h-4 px-1 rounded-full ${BADGE_CLASS} text-white text-[9px] font-bold flex items-center justify-center leading-none`}>
              {count}
            </span>
          )}
          <Icon className="w-5 h-5" />
          <span className="text-[9px] font-bold tracking-wide">{label}</span>
        </button>
        );
      })}
      <div className="mt-auto relative">
        {/* Placeholder for custom features. A native alert() here blocked the whole
            tab (and looked broken in demos); a small popover says the same thing. */}
        <button
          onClick={() => setSoon((v) => !v)}
          onBlur={() => setSoon(false)}
          title="Fitur kustom"
          className="w-10 h-10 bg-[#14543e] hover:bg-[#104230] rounded-xl flex items-center justify-center transition-colors"
        >
          <Plus className="w-5 h-5 text-white" />
        </button>
        {soon && (
          <div className="absolute left-12 bottom-0 w-56 rounded-xl border border-gray-100 bg-white px-3 py-2 shadow-lg text-[11px] leading-snug text-gray-600 z-50">
            <p className="font-bold text-gray-800 mb-0.5">Fitur kustom</p>
            Masih dalam tahap riset dan pengembangan. Segera hadir.
          </div>
        )}
      </div>
    </aside>
  );
}
